import { useEffect, useMemo, useRef, useState, Fragment } from "react";
import { toast } from "sonner";
import { Target, Warning, CheckCircle, Clock, Trash, PaperPlaneTilt, X, ChatCircleDots } from "@phosphor-icons/react";
import Layout from "../../components/Layout";
import api from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";

/* --- Local format helpers --- */
function fmtDateTime(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-IN", {
      day: "2-digit", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
}

function relativeFromNow(iso, now) {
  if (!iso) return "";
  const target = new Date(iso).getTime();
  const diff = target - now.getTime();
  const abs = Math.abs(diff);
  const sign = diff < 0 ? "ago" : "left";
  const m = Math.floor(abs / 60000);
  const h = Math.floor(m / 60);
  const d = Math.floor(h / 24);
  if (m < 1) return `now`;
  if (m < 60) return `${m}m ${sign}`;
  if (h < 24) return `${h}h ${m % 60}m ${sign}`;
  return `${d}d ${h % 24}h ${sign}`;
}

const STATUS_STYLE = {
  pending: { c: "#F4A261", bg: "rgba(244,162,97,0.12)", label: "PENDING" },
  met:     { c: "#0D9488", bg: "rgba(13,148,136,0.12)", label: "MET" },
  not_met: { c: "#D90429", bg: "rgba(217,4,41,0.12)",  label: "NOT MET" },
};

const emptyForm = { description: "", owner_name: "", owner_email: "", deadline: "" };

export default function Tracker() {
  const { user } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [overdueUnacked, setOverdueUnacked] = useState([]);
  const [users, setUsers] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [submitting, setSubmitting] = useState(false);
  const [now, setNow] = useState(new Date());
  const [expandedId, setExpandedId] = useState(null);
  const [updateText, setUpdateText] = useState("");
  const [hudTask, setHudTask] = useState(null);
  const [hudText, setHudText] = useState("");
  const [hudSubmitting, setHudSubmitting] = useState(false);
  const ownerInputRef = useRef(null);
  const [ownerOpen, setOwnerOpen] = useState(false);

  /* live clock + auto-reconcile */
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);

  /* polling for tasks every 15s — picks up overdue flips even when tab idle */
  useEffect(() => {
    const id = setInterval(() => load(), 15000);
    return () => clearInterval(id);
  }, []);

  const load = async () => {
    try {
      const { data } = await api.get("/tracker/tasks");
      setTasks(data.tasks || []);
      setOverdueUnacked(data.overdue_unacked || []);
      // Functional setState avoids stale-closure capture of hudTask
      setHudTask((prev) => prev || (data.overdue_unacked || [])[0] || null);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Failed to load tasks";
      if (err?.response?.status !== 403) toast.error(msg);
    }
  };

  const loadUsers = async () => {
    try {
      const { data } = await api.get("/tracker/users");
      setUsers(data || []);
    } catch {
      // Non-critical: autocomplete will simply show no suggestions.
      // No user-facing toast or log needed.
    }
  };

  useEffect(() => { load(); loadUsers(); }, []);

  /* Autocomplete suggestions for assignee */
  const ownerSuggestions = useMemo(() => {
    const q = (form.owner_name || "").trim().toLowerCase();
    if (!q) return users.slice(0, 6);
    return users
      .filter((u) => `${u.name} ${u.email}`.toLowerCase().includes(q))
      .slice(0, 6);
  }, [users, form.owner_name]);

  /* Counts (memoised) */
  const counts = useMemo(() => {
    const c = { total: tasks.length, pending: 0, met: 0, not_met: 0 };
    tasks.forEach((t) => { c[t.status] = (c[t.status] || 0) + 1; });
    return c;
  }, [tasks]);

  const sortedTasks = useMemo(() => {
    const rank = { not_met: 0, pending: 1, met: 2 };
    return [...tasks].sort((a, b) => {
      const r = (rank[a.status] ?? 3) - (rank[b.status] ?? 3);
      if (r !== 0) return r;
      return new Date(a.deadline).getTime() - new Date(b.deadline).getTime();
    });
  }, [tasks]);

  const create = async () => {
    if (!form.description.trim()) { toast.error("Task description required"); return; }
    if (!form.owner_name.trim()) { toast.error("Assignee required"); return; }
    if (!form.deadline) { toast.error("Deadline required"); return; }
    setSubmitting(true);
    try {
      // Convert local datetime-local string → ISO with timezone
      const iso = new Date(form.deadline).toISOString();
      await api.post("/tracker/tasks", {
        description: form.description.trim(),
        owner_name: form.owner_name.trim(),
        owner_email: form.owner_email.trim(),
        deadline: iso,
      });
      toast.success("Task deployed & tracking");
      setForm(emptyForm);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to create task");
    } finally {
      setSubmitting(false);
    }
  };

  const markMet = async (task) => {
    try {
      await api.put(`/tracker/tasks/${task.id}`, { status: "met" });
      toast.success("Task marked MET");
      load();
    } catch (err) {
      toast.error("Failed to update status");
    }
  };

  const reopenTask = async (task) => {
    if (!window.confirm("Reopen this task as Pending?")) return;
    try {
      await api.put(`/tracker/tasks/${task.id}`, { status: "pending", acknowledged: false });
      toast.success("Task reopened");
      load();
    } catch {
      toast.error("Failed to reopen");
    }
  };

  const removeTask = async (task) => {
    if (!window.confirm(`Delete task "${task.description}"?`)) return;
    try {
      await api.delete(`/tracker/tasks/${task.id}`);
      toast.success("Task deleted");
      load();
    } catch {
      toast.error("Failed to delete");
    }
  };

  const addUpdate = async (task, kind = "progress") => {
    const text = updateText.trim();
    if (!text) { toast.error("Write a progress note first"); return; }
    try {
      await api.post(`/tracker/tasks/${task.id}/updates`, { text, kind });
      setUpdateText("");
      toast.success("Update logged");
      load();
    } catch {
      toast.error("Failed to add update");
    }
  };

  const submitHud = async () => {
    if (!hudTask) return;
    const text = hudText.trim();
    if (!text) { toast.error("Clarification text is required to dismiss this escalation."); return; }
    setHudSubmitting(true);
    try {
      await api.post(`/tracker/tasks/${hudTask.id}/updates`, { text, kind: "delay" });
      toast.success("Clarification submitted · escalation acknowledged");
      setHudText("");
      setHudTask(null);
      load();
    } catch {
      toast.error("Failed to submit");
    } finally {
      setHudSubmitting(false);
    }
  };

  const pickOwner = (u) => {
    setForm((f) => ({ ...f, owner_name: u.name || u.email, owner_email: u.email || "" }));
    setOwnerOpen(false);
    ownerInputRef.current?.blur();
  };

  return (
    <Layout
      title="Shelves-Track"
      subtitle="Task monitoring engine · escalates breached deadlines"
      actions={
        <div className="flex items-center gap-3">
          <div className="font-mono-shelv text-[10px] md:text-xs tracking-widest text-[#717680] flex items-center gap-1.5 md:gap-2" data-testid="tracker-live-clock">
            <span className="w-1.5 h-1.5 rounded-full bg-[#D90429] animate-vital" />
            <span className="hidden sm:inline">{now.toLocaleString("en-IN", { hour: "2-digit", minute: "2-digit", second: "2-digit", day: "2-digit", month: "short" })}</span>
            <span className="sm:hidden">{now.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })}</span>
          </div>
        </div>
      }
    >
      {/* Stats strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 md:gap-3 mb-5 md:mb-6" data-testid="tracker-stats">
        {[
          { l: "Total Tasks", v: counts.total, c: "#0A0A0A", Icon: Target },
          { l: "Pending",     v: counts.pending, c: "#F4A261", Icon: Clock },
          { l: "Met",         v: counts.met, c: "#0D9488", Icon: CheckCircle },
          { l: "Not Met",     v: counts.not_met, c: "#D90429", Icon: Warning },
        ].map((s) => (
          <div key={s.l} className="border border-black/10 bg-white p-3 md:p-4 flex items-center justify-between">
            <div className="min-w-0">
              <div className="overline text-[9px] md:text-[11px]">{s.l}</div>
              <div className="font-display text-2xl md:text-3xl font-black tracking-tighter mt-1" style={{ color: s.c }} data-testid={`stat-${s.l.toLowerCase().replace(" ","-")}`}>{s.v}</div>
            </div>
            <s.Icon size={22} weight="duotone" style={{ color: s.c }} className="flex-shrink-0" />
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 md:gap-6">
        {/* ASSIGN NEW TASK FORM */}
        <div className="lg:col-span-1">
          <div className="border border-black/10 bg-white p-4 md:p-5 lg:sticky lg:top-4">
            <div className="overline mb-1">Assign new task</div>
            <h3 className="font-display text-xl font-bold tracking-tight text-[#0A0A0A] mb-4">Deploy &amp; Track</h3>

            <div className="space-y-4">
              <div>
                <Label>Task Description</Label>
                <Textarea
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  rows={2}
                  placeholder="e.g., Deploy server updates for AIIMS"
                  className="rounded-none"
                  data-testid="task-description-input"
                />
              </div>

              <div className="relative">
                <Label>Assignee</Label>
                <Input
                  ref={ownerInputRef}
                  value={form.owner_name}
                  onChange={(e) => { setForm({ ...form, owner_name: e.target.value }); setOwnerOpen(true); }}
                  onFocus={() => setOwnerOpen(true)}
                  onBlur={() => setTimeout(() => setOwnerOpen(false), 200)}
                  placeholder="Type a name or pick from team"
                  className="rounded-none"
                  data-testid="task-owner-input"
                />
                {ownerOpen && ownerSuggestions.length > 0 && (
                  <div className="absolute z-20 left-0 right-0 mt-1 border border-black/15 bg-white shadow-lg max-h-56 overflow-y-auto" data-testid="owner-autocomplete">
                    {ownerSuggestions.map((u) => (
                      <button
                        key={u.email}
                        type="button"
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => pickOwner(u)}
                        className="w-full text-left px-3 py-2 hover:bg-[#FFE5E9]/40 border-b border-black/5 last:border-b-0"
                        data-testid={`owner-suggest-${u.email}`}
                      >
                        <div className="text-sm font-semibold text-[#0A0A0A]">{u.name || u.email}</div>
                        <div className="text-[11px] text-[#717680] flex items-center gap-1.5">
                          <span>{u.email}</span>
                          {u.phone ? (
                            <span className="text-[9px] font-mono-shelv tracking-wider text-[#0D9488]" title="WhatsApp fallback available">· 📱 WA-ready</span>
                          ) : (
                            <span className="text-[9px] font-mono-shelv tracking-wider text-[#D90429]" title="No phone on file — WhatsApp fallback unavailable">· ⚠ no phone</span>
                          )}
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <div>
                <Label>Deadline</Label>
                <Input
                  type="datetime-local"
                  value={form.deadline}
                  onChange={(e) => setForm({ ...form, deadline: e.target.value })}
                  className="rounded-none"
                  data-testid="task-deadline-input"
                />
              </div>

              <button
                onClick={create}
                disabled={submitting}
                className="w-full inline-flex items-center justify-center gap-2 bg-[#D90429] text-white px-4 py-3 font-semibold hover:bg-[#EF233C] transition-all active:scale-95 disabled:opacity-50"
                data-testid="deploy-task-button"
              >
                <PaperPlaneTilt size={16} weight="bold" /> {submitting ? "Deploying…" : "Deploy & Track Task"}
              </button>
            </div>
          </div>
        </div>

        {/* WATCHLIST */}
        <div className="lg:col-span-2">
          <div className="border border-black/10 bg-white">
            <div className="px-4 md:px-5 py-3 md:py-4 border-b border-black/10 flex items-center justify-between gap-3">
              <div className="min-w-0">
                <div className="overline">Active Watchlist</div>
                <div className="font-display text-base md:text-xl font-bold tracking-tight truncate">All tasks in flight</div>
              </div>
              {overdueUnacked.length > 0 && (
                <button
                  onClick={() => setHudTask(overdueUnacked[0])}
                  className="inline-flex items-center gap-1.5 md:gap-2 px-2.5 md:px-3 py-2 bg-[#D90429] text-white text-[10px] md:text-xs font-bold animate-pulse flex-shrink-0"
                  data-testid="show-hud-button"
                >
                  <Warning size={14} weight="bold" /> {overdueUnacked.length} BREACH{overdueUnacked.length === 1 ? "" : "ES"}
                </button>
              )}
            </div>

            {sortedTasks.length === 0 ? (
              <div className="px-5 py-16 text-center text-[#717680]" data-testid="empty-state">
                <Target size={32} weight="duotone" className="mx-auto text-[#D90429]/40 mb-2" />
                No tasks yet. Deploy one from the form above.
              </div>
            ) : (
              <>
              {/* MOBILE: card list */}
              <div className="md:hidden divide-y divide-black/5" data-testid="tasks-cards">
                {sortedTasks.map((t) => {
                  const stl = STATUS_STYLE[t.status] || STATUS_STYLE.pending;
                  const lastUpdate = (t.updates || [])[(t.updates || []).length - 1];
                  const isOverdueUnacked = t.status === "not_met" && !t.acknowledged;
                  const expanded = expandedId === t.id;
                  return (
                    <div
                      key={t.id}
                      className={`p-4 ${isOverdueUnacked ? "bg-[#FFE5E9]/40" : ""}`}
                      data-testid={`task-card-${t.id}`}
                    >
                      <div className="flex items-start justify-between gap-2 mb-2">
                        <button
                          onClick={() => setExpandedId(expanded ? null : t.id)}
                          className="text-left flex-1 min-w-0"
                        >
                          <div className="font-semibold text-[#0A0A0A] text-sm break-words">{t.description}</div>
                          <div className="text-[10px] text-[#717680] font-mono-shelv mt-1">
                            {t.owner_name}{t.owner_email ? ` · ${t.owner_email}` : ""}
                          </div>
                        </button>
                        <span
                          className="inline-flex items-center px-1.5 py-0.5 text-[9px] font-mono-shelv tracking-widest font-bold border whitespace-nowrap"
                          style={{ background: stl.bg, color: stl.c, borderColor: stl.c + "40" }}
                          data-testid={`status-badge-${t.id}`}
                        >
                          {stl.label}
                        </span>
                      </div>

                      <div className="flex items-center justify-between text-[11px] mb-2">
                        <div>
                          <span className="text-[#717680]">Due: </span>
                          <span className="text-[#0A0A0A]">{fmtDateTime(t.deadline)}</span>
                        </div>
                        <span className="font-mono-shelv" style={{ color: stl.c }}>
                          {relativeFromNow(t.deadline, now)}
                        </span>
                      </div>

                      {lastUpdate && (
                        <div className="text-[11px] text-[#4A4D57] mb-3 line-clamp-2">
                          <span className="font-mono-shelv text-[9px] uppercase tracking-widest" style={{ color: lastUpdate.kind === "delay" ? "#D90429" : "#0A0A0A" }}>
                            {lastUpdate.kind}
                          </span> · {lastUpdate.text}
                        </div>
                      )}

                      <div className="flex items-center gap-2">
                        {t.status !== "met" ? (
                          <button
                            onClick={() => markMet(t)}
                            className="flex-1 inline-flex items-center justify-center gap-1 px-2 py-2 bg-[#0D9488] text-white text-[11px] font-bold"
                            data-testid={`mark-met-${t.id}`}
                          >
                            <CheckCircle size={12} weight="bold" /> Mark MET
                          </button>
                        ) : (
                          <button
                            onClick={() => reopenTask(t)}
                            className="flex-1 inline-flex items-center justify-center gap-1 px-2 py-2 border border-black/15 text-[11px]"
                            data-testid={`reopen-${t.id}`}
                          >
                            Reopen
                          </button>
                        )}
                        <button
                          onClick={() => setExpandedId(expanded ? null : t.id)}
                          className="inline-flex items-center justify-center gap-1 px-3 py-2 border border-black/15 text-[11px]"
                          data-testid={`toggle-detail-${t.id}`}
                        >
                          <ChatCircleDots size={12} weight="bold" /> {t.updates?.length || 0}
                        </button>
                        <button
                          onClick={() => removeTask(t)}
                          className="p-2 text-[#D90429] border border-[#D90429]/30"
                          data-testid={`delete-task-${t.id}`}
                          aria-label="Delete task"
                        >
                          <Trash size={12} weight="bold" />
                        </button>
                      </div>

                      {expanded && (
                        <div className="mt-3 pt-3 border-t border-black/10" data-testid={`task-detail-${t.id}`}>
                          <div className="overline mb-2 flex items-center gap-2 text-[9px]"><ChatCircleDots size={12} weight="bold" /> Updates timeline</div>
                          <div className="space-y-2 mb-3 max-h-56 overflow-y-auto">
                            {(t.updates || []).length === 0 && <div className="text-xs text-[#9CA3AF]">No updates yet.</div>}
                            {(t.updates || []).slice().reverse().map((u) => (
                              <div
                                key={u.id}
                                className="border-l-2 pl-2 py-1"
                                style={{ borderLeftColor: u.kind === "delay" ? "#D90429" : "#0A0A0A" }}
                                data-testid={`update-${u.id}`}
                              >
                                <div className="text-[9px] text-[#717680] font-mono-shelv tracking-widest uppercase">
                                  {fmtDateTime(u.created_at)} · {u.kind}
                                </div>
                                <div className="text-xs text-[#0A0A0A] mt-0.5 whitespace-pre-wrap break-words">{u.text}</div>
                              </div>
                            ))}
                          </div>
                          <Textarea
                            value={updateText}
                            onChange={(e) => setUpdateText(e.target.value)}
                            placeholder="Add a progress note…"
                            rows={2}
                            className="rounded-none mb-2"
                            data-testid={`update-input-${t.id}`}
                          />
                          <button
                            onClick={() => addUpdate(t, "progress")}
                            className="w-full px-3 py-2 bg-[#0A0A0A] text-white text-xs font-semibold"
                            data-testid={`post-update-${t.id}`}
                          >
                            Post update
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* DESKTOP: table */}
              <div className="hidden md:block overflow-x-auto">
                <table className="w-full text-sm min-w-[640px]" data-testid="tasks-table">
                  <thead className="bg-[#F5F6F8]">
                    <tr className="text-left">
                      {["Task", "Assignee", "Deadline", "Status", "Last update", ""].map((h) => (
                        <th key={h} className="px-4 py-3 font-mono-shelv text-[10px] uppercase tracking-widest text-[#717680]">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {sortedTasks.map((t) => {
                      const stl = STATUS_STYLE[t.status] || STATUS_STYLE.pending;
                      const lastUpdate = (t.updates || [])[(t.updates || []).length - 1];
                      const isOverdueUnacked = t.status === "not_met" && !t.acknowledged;
                      const expanded = expandedId === t.id;
                      return (
                        <Fragment key={t.id}>
                          <tr
                            className={`border-t border-black/5 hover:bg-[#FAFBFC] ${isOverdueUnacked ? "bg-[#FFE5E9]/40" : ""}`}
                            data-testid={`task-row-${t.id}`}
                          >
                            <td className="px-4 py-3">
                              <button onClick={() => setExpandedId(expanded ? null : t.id)} className="text-left">
                                <div className="font-semibold text-[#0A0A0A]">{t.description}</div>
                                <div className="text-[10px] text-[#717680] font-mono-shelv mt-0.5">
                                  {(t.updates?.length || 0) > 0 && <span className="inline-flex items-center gap-1"><ChatCircleDots size={10} weight="bold" /> {t.updates.length} update{t.updates.length === 1 ? "" : "s"}</span>}
                                </div>
                              </button>
                            </td>
                            <td className="px-4 py-3 text-[#4A4D57]">
                              <div className="text-sm font-medium">{t.owner_name}</div>
                              {t.owner_email && <div className="text-[10px] text-[#717680]">{t.owner_email}</div>}
                            </td>
                            <td className="px-4 py-3">
                              <div className="text-[#0A0A0A]">{fmtDateTime(t.deadline)}</div>
                              <div className="text-[10px] font-mono-shelv mt-0.5" style={{ color: stl.c }}>
                                {relativeFromNow(t.deadline, now)}
                              </div>
                            </td>
                            <td className="px-4 py-3">
                              <span
                                className="inline-flex items-center px-2 py-1 text-[10px] font-mono-shelv tracking-widest font-bold border"
                                style={{ background: stl.bg, color: stl.c, borderColor: stl.c + "40" }}
                                data-testid={`status-badge-${t.id}`}
                              >
                                {stl.label}{isOverdueUnacked && " · UNACKED"}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-[12px] text-[#4A4D57]">
                              {lastUpdate ? (
                                <div className="max-w-[200px] truncate" title={lastUpdate.text}>
                                  <span className="font-mono-shelv text-[10px] uppercase tracking-widest" style={{ color: lastUpdate.kind === "delay" ? "#D90429" : "#0A0A0A" }}>
                                    {lastUpdate.kind}
                                  </span> · {lastUpdate.text}
                                </div>
                              ) : <span className="text-[#9CA3AF] italic">no updates</span>}
                            </td>
                            <td className="px-4 py-3 text-right whitespace-nowrap">
                              {t.status !== "met" ? (
                                <button
                                  onClick={() => markMet(t)}
                                  className="inline-flex items-center gap-1 px-2.5 py-1.5 bg-[#0D9488] text-white text-[11px] font-bold hover:bg-[#0F766E]"
                                  data-testid={`mark-met-${t.id}`}
                                >
                                  <CheckCircle size={12} weight="bold" /> Mark MET
                                </button>
                              ) : (
                                <button
                                  onClick={() => reopenTask(t)}
                                  className="inline-flex items-center gap-1 px-2.5 py-1.5 border border-black/15 text-[11px] hover:bg-black/5"
                                  data-testid={`reopen-${t.id}`}
                                >
                                  Reopen
                                </button>
                              )}
                              <button
                                onClick={() => removeTask(t)}
                                className="ml-1 p-1.5 text-[#D90429] hover:bg-[#D90429]/10"
                                data-testid={`delete-task-${t.id}`}
                              >
                                <Trash size={12} weight="bold" />
                              </button>
                            </td>
                          </tr>
                          {expanded && (
                            <tr className="bg-[#FAFBFC] border-t border-black/5" data-testid={`task-detail-${t.id}`}>
                              <td colSpan={6} className="px-4 py-4">
                                <div className="overline mb-2 flex items-center gap-2"><ChatCircleDots size={12} weight="bold" /> Updates timeline</div>
                                <div className="space-y-2 mb-3 max-h-56 overflow-y-auto">
                                  {(t.updates || []).length === 0 && <div className="text-xs text-[#9CA3AF]">No updates yet.</div>}
                                  {(t.updates || []).slice().reverse().map((u) => (
                                    <div
                                      key={u.id}
                                      className="border-l-2 pl-3 py-1.5"
                                      style={{ borderLeftColor: u.kind === "delay" ? "#D90429" : "#0A0A0A" }}
                                      data-testid={`update-${u.id}`}
                                    >
                                      <div className="text-[10px] text-[#717680] font-mono-shelv tracking-widest uppercase">
                                        {fmtDateTime(u.created_at)} · {u.author_name || u.author_email} · {u.kind}
                                      </div>
                                      <div className="text-sm text-[#0A0A0A] mt-0.5 whitespace-pre-wrap">{u.text}</div>
                                    </div>
                                  ))}
                                </div>
                                <div className="flex gap-2">
                                  <Textarea
                                    value={updateText}
                                    onChange={(e) => setUpdateText(e.target.value)}
                                    placeholder="Add a progress note…"
                                    rows={2}
                                    className="rounded-none flex-1"
                                    data-testid={`update-input-${t.id}`}
                                  />
                                  <button
                                    onClick={() => addUpdate(t, "progress")}
                                    className="self-stretch px-3 bg-[#0A0A0A] text-white text-xs font-semibold hover:bg-[#D90429]"
                                    data-testid={`post-update-${t.id}`}
                                  >
                                    Post update
                                  </button>
                                </div>
                              </td>
                            </tr>
                          )}
                        </Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* HUD MODAL — escalation for breached deadlines */}
      <Dialog open={!!hudTask} onOpenChange={(open) => { if (!open && hudTask?.acknowledged) setHudTask(null); }}>
        <DialogContent
          className="max-w-lg rounded-none border-t-[10px] border-t-[#D90429] [&>button]:hidden"
          data-testid="hud-modal"
        >
          <DialogHeader>
            <DialogTitle className="text-[#D90429] font-display text-2xl font-black tracking-tighter flex items-center gap-2">
              <Warning size={28} weight="fill" /> TIMELINE BREACH DETECTED
            </DialogTitle>
          </DialogHeader>
          {hudTask && (
            <>
              <p className="text-sm text-[#4A4D57] -mt-2">
                This task has missed its deadline and requires immediate clarification before this window can be dismissed.
              </p>
              <div className="border-t border-b border-black/10 py-3 my-2 space-y-1 text-sm">
                <div><span className="text-[#717680] text-xs uppercase tracking-widest font-mono-shelv">Task</span><div className="font-bold text-[#0A0A0A]">{hudTask.description}</div></div>
                <div><span className="text-[#717680] text-xs uppercase tracking-widest font-mono-shelv">Owner</span><div className="text-[#0A0A0A]">{hudTask.owner_name}{hudTask.owner_email ? ` · ${hudTask.owner_email}` : ""}</div></div>
                <div><span className="text-[#717680] text-xs uppercase tracking-widest font-mono-shelv">Missed deadline</span><div className="text-[#D90429] font-mono-shelv">{fmtDateTime(hudTask.deadline)} · {relativeFromNow(hudTask.deadline, now)}</div></div>
              </div>
              <div>
                <Label>Reason for delay &amp; new ETA</Label>
                <Textarea
                  value={hudText}
                  onChange={(e) => setHudText(e.target.value)}
                  rows={3}
                  placeholder="State reason for delay and new estimated completion time…"
                  className="rounded-none"
                  data-testid="hud-text-input"
                />
              </div>
              <div className="flex items-center gap-2 mt-4">
                <button
                  onClick={submitHud}
                  disabled={hudSubmitting || !hudText.trim()}
                  className="flex-1 inline-flex items-center justify-center gap-2 bg-[#D90429] text-white px-4 py-3 font-semibold hover:bg-[#EF233C] disabled:opacity-50"
                  data-testid="hud-submit-button"
                >
                  {hudSubmitting ? "Submitting…" : "Submit Update & Acknowledge"}
                </button>
                <button
                  onClick={() => setHudTask(null)}
                  title="Snooze · this breach will re-appear on next refresh"
                  className="px-3 py-3 border border-black/15 text-[#717680] hover:bg-black/5"
                  data-testid="hud-snooze-button"
                >
                  <X size={16} />
                </button>
              </div>
              <div className="text-[10px] font-mono-shelv text-[#9CA3AF] tracking-widest uppercase mt-2">
                Acknowledged by · {user?.email}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
